import React, { useState } from 'react';
import { Eye, EyeOff, Copy, CheckCircle, KeyRound, Plus, Loader2, X, RefreshCw } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '../lib/tenant-api';

interface PreviewKeyManagerProps {
  onCreatePreviewKey?: () => void;
}

export const PreviewKeyManager: React.FC<PreviewKeyManagerProps> = ({ onCreatePreviewKey }) => {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reloading, setReloading] = useState(false);
  const queryClient = useQueryClient();

  const { data: previewKey, isLoading } = useQuery({
    queryKey: ['tenantPreviewApiKey'],
    queryFn: () => tenantApi.getPreviewApiKey(),
  });

  const deleteMutation = useMutation({
    mutationFn: () => tenantApi.deletePreviewApiKey(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantPreviewApiKey'] });
      setConfirmDelete(false);
      window.dispatchEvent(new Event('widget-api-key-changed'));
    },
  });

  const maskKey = (key: string) => {
    if (!key) return '';
    return `${key.slice(0, 10)}${'•'.repeat(16)}${key.slice(-4)}`;
  };

  const handleReload = () => {
    setReloading(true);
    window.dispatchEvent(new CustomEvent('widget-config-changed'));
    setTimeout(() => setReloading(false), 1500);
  };

  const handleCopy = async () => {
    if (previewKey?.plain_key) {
      try {
        await navigator.clipboard.writeText(previewKey.plain_key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // silently fail
      }
    }
  };

  return (
    <div className="border border-gray-200 bg-gray-50 px-4 py-3 flex items-center gap-3 flex-wrap">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">
        Widget Preview Key
      </span>

      {isLoading && (
        <div className="flex items-center gap-1.5 text-gray-400">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          <span className="text-xs">Loading...</span>
        </div>
      )}

      {!isLoading && previewKey?.plain_key && (
        <>
          <div className="flex items-center gap-1.5 bg-white border border-gray-200 px-2.5 py-1 flex-1 min-w-0">
            <KeyRound className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <span className="font-mono text-xs text-gray-700 truncate flex-1">
              {showKey ? previewKey.plain_key : maskKey(previewKey.plain_key)}
            </span>
            <button type="button" onClick={handleCopy} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title="Copy">
              {copied ? <CheckCircle className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
            <button type="button" onClick={() => setShowKey(!showKey)} className="text-gray-400 hover:text-gray-600 flex-shrink-0" title={showKey ? 'Hide' : 'Show'}>
              {showKey ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>

          {!confirmDelete ? (
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                data-feature-id="preview-reload-button"
                onClick={handleReload}
                disabled={reloading}
                title="Reload preview widget"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-indigo-600 bg-white border border-indigo-200 hover:bg-indigo-50 disabled:opacity-50 whitespace-nowrap"
              >
                <RefreshCw className={`h-3 w-3 ${reloading ? 'animate-spin' : ''}`} />
                {reloading ? 'Reloading…' : 'Reload Preview Widget'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(true)}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-100 whitespace-nowrap"
              >
                <X className="h-3 w-3" /> Unset
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-gray-600">Unset preview key?</span>
              <button
                type="button"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isLoading}
                className="px-2 py-0.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50"
              >
                No
              </button>
            </div>
          )}
        </>
      )}

      {!isLoading && !previewKey?.plain_key && (
        <>
          <span className="text-xs text-gray-400 flex-1">No preview key set</span>
          {onCreatePreviewKey && (
            <button
              type="button"
              onClick={onCreatePreviewKey}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
            >
              <Plus className="h-3.5 w-3.5" /> Create Preview API Key
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default PreviewKeyManager;
